import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { collection, doc, getDoc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { db, storage } from "../Config/firebase";
import { consultaTenantAtual, getUsuarioAtualComTenant } from "../Utils/tenant";

const COLLECTION = "tblListasPresencaTreinamentos";
const ROTA = "/app/listas-presenca-treinamentos";
const TIPOS = { "application/pdf": "pdf", "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };

function dataBR(valor) {
  const [ano, mes, dia] = String(valor || "").split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : "-";
}

export default function CadastroListaPresenca() {
  const { registroId } = useParams();
  const history = useHistory();
  const [registros, setRegistros] = useState([]);
  const [ficha, setFicha] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [arquivo, setArquivo] = useState(null);
  const documentoNovo = useRef(null);
  const uploadPendente = useRef(null);
  const bloqueio = useRef(false);
  const ultimaFichaSalva = useRef("");

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setFicha(null);
    setArquivo(null);
    uploadPendente.current = null;
    documentoNovo.current = null;
    setAviso(registroId && ultimaFichaSalva.current === registroId ? { tipo: "success", texto: "Lista de presença salva com sucesso." } : null);
    async function carregar() {
      try {
        if (!registroId) {
          const snap = await getDocs(consultaTenantAtual(collection(db, COLLECTION)));
          const lista = snap.docs.map((item) => ({ ...item.data(), id: item.id }));
          lista.sort((a, b) => String(b.dataTreinamento || "").localeCompare(String(a.dataTreinamento || "")));
          if (ativo) setRegistros(lista);
        } else if (registroId === "novo") {
          documentoNovo.current = doc(collection(db, COLLECTION));
          if (ativo) setFicha({ assunto: "", conteudoProgramatico: "", dataTreinamento: "", local: "", instrutor: "", anexo: null });
        } else {
          const usuario = await getUsuarioAtualComTenant();
          const snap = await getDoc(doc(db, COLLECTION, registroId));
          if (!snap.exists()) throw new Error("Registro não encontrado.");
          const dados = snap.data();
          if (!usuario.isSuperadmin && (!usuario.tenantId || dados.tenantId !== usuario.tenantId)) throw new Error("Registro de outra empresa.");
          if (ativo) setFicha({ ...dados, id: snap.id });
        }
      } catch (error) {
        console.error("Erro ao carregar listas de presença:", error);
        if (ativo) setAviso({ tipo: "error", texto: "Não foi possível carregar os dados. Verifique sua conexão e as permissões de acesso." });
      } finally {
        if (ativo) setCarregando(false);
      }
    }
    carregar();
    return () => { ativo = false; };
  }, [registroId]);

  function selecionarArquivo(event) {
    const selecionado = event.target.files?.[0];
    event.target.value = "";
    if (!selecionado) return;
    const limite = selecionado.type === "application/pdf" ? 100 : 5;
    if (!TIPOS[selecionado.type] || selecionado.size === 0 || selecionado.size >= limite * 1024 * 1024) {
      setAviso({ tipo: "error", texto: "Selecione um PDF menor que 100 MB ou uma imagem JPG, PNG ou WebP menor que 5 MB. O arquivo não pode estar vazio." });
      return;
    }
    setArquivo(selecionado);
    uploadPendente.current = null;
    setAviso(null);
  }

  async function salvar() {
    if (!ficha || bloqueio.current) return;
    if (![ficha.assunto, ficha.conteudoProgramatico, ficha.dataTreinamento, ficha.local, ficha.instrutor].every((valor) => String(valor || "").trim())) {
      setAviso({ tipo: "error", texto: "Informe assunto, conteúdo programático, data de realização, local e instrutor." });
      return;
    }
    bloqueio.current = true;
    setSalvando(true);
    setAviso(null);
    try {
      const usuario = await getUsuarioAtualComTenant();
      const tenantId = ficha.tenantId || usuario.tenantId || (usuario.isSuperadmin ? localStorage.getItem("tenantId") : "");
      if (!tenantId) throw new Error("Selecione uma empresa antes de salvar.");
      if (!usuario.isSuperadmin && tenantId !== usuario.tenantId) throw new Error("Empresa divergente.");
      const documento = ficha.id ? doc(db, COLLECTION, ficha.id) : documentoNovo.current;
      let anexo = ficha.anexo || null;
      if (arquivo) {
        if (!uploadPendente.current) {
          const uploadId = doc(collection(db, COLLECTION)).id;
          uploadPendente.current = { caminho: `tenants/${tenantId}/listas-presenca-treinamentos/${documento.id}/${uploadId}.${TIPOS[arquivo.type]}` };
        }
        const pendente = uploadPendente.current;
        if (!pendente.anexo) {
          const arquivoRef = ref(storage, pendente.caminho);
          await uploadBytes(arquivoRef, arquivo, { contentType: arquivo.type });
          pendente.anexo = { nome: arquivo.name, caminho: pendente.caminho, url: await getDownloadURL(arquivoRef), tipo: arquivo.type, tamanho: arquivo.size };
        }
        anexo = pendente.anexo;
      }
      const dados = {
        assunto: ficha.assunto.trim(), conteudoProgramatico: ficha.conteudoProgramatico.trim(), dataTreinamento: ficha.dataTreinamento,
        local: ficha.local.trim(), instrutor: ficha.instrutor.trim(),
        anexo, tenantId, codigoDocumento: "RQ 1.12", atualizadoEm: new Date(),
      };
      if (ficha.id) await updateDoc(documento, dados);
      else await setDoc(documento, { ...dados, criadoEm: new Date() });
      setFicha((atual) => ({ ...atual, ...dados, id: documento.id }));
      setArquivo(null);
      uploadPendente.current = null;
      ultimaFichaSalva.current = documento.id;
      history.replace(ROTA);
      setAviso({ tipo: "success", texto: "Lista de presença salva com sucesso." });
    } catch (error) {
      console.error("Erro ao salvar lista de presença:", error);
      setAviso({ tipo: "error", texto: "Não foi possível salvar. Verifique sua conexão, a empresa selecionada e as permissões de Firestore e Storage. Seus dados foram mantidos para tentar novamente." });
    } finally {
      bloqueio.current = false;
      setSalvando(false);
    }
  }

  const termo = filtro.trim().toLocaleLowerCase("pt-BR");
  const filtrados = registros.filter((item) => [item.assunto, dataBR(item.dataTreinamento), item.local, item.instrutor].some((valor) => contemTextoPesquisa(String(valor || "").toLocaleLowerCase("pt-BR"), termo)));
  const campo = (nome, label, props = {}) => <TextField label={label} value={ficha[nome] || ""} onChange={(event) => { setAviso(null); setFicha((atual) => ({ ...atual, [nome]: event.target.value })); }} disabled={salvando} required fullWidth {...props} />;

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, mb: 2, p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
            <Box><Typography variant="h5" fontWeight={700}>Lista de Presença em Treinamentos</Typography><Typography color="text.secondary">RQ 1.12 — {registroId ? "Dados do treinamento e lista assinada" : "Listas cadastradas"}</Typography></Box>
            {registroId ? <Stack direction="row" spacing={1}><Button variant="outlined" disabled={salvando} onClick={() => history.push(ROTA)}>Voltar</Button><Button variant="contained" disabled={salvando || carregando || !ficha} onClick={salvar}>{salvando ? "Salvando..." : "Salvar"}</Button></Stack> : <Button variant="contained" onClick={() => history.push(`${ROTA}/novo`)}>Nova lista</Button>}
          </Stack>
          {!registroId && <TextField label="Pesquisar por assunto, data, local ou instrutor" value={filtro} onChange={(event) => setFiltro(event.target.value)} fullWidth size="small" sx={{ mt: 2 }} />}
        </Paper>
        {aviso && <Alert severity={aviso.tipo} sx={{ mb: 2 }}>{aviso.texto}</Alert>}
        {carregando && <Stack alignItems="center" sx={{ p: 4 }}><CircularProgress /></Stack>}
        {!carregando && !registroId && <Paper sx={{ border: "1px solid #e2e8f0", overflow: "auto" }}><Table size="small" sx={{ minWidth: 800 }}>
          <TableHead sx={{ backgroundColor: "#f8fafc" }}><TableRow>{["Data de realização", "Assunto", "Local", "Instrutor", "Lista assinada", "Ações"].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>{filtrados.map((item) => <TableRow key={item.id} hover sx={{ cursor: "pointer" }} onClick={() => history.push(`${ROTA}/${item.id}`)}><TableCell sx={{ whiteSpace: "nowrap" }}>{dataBR(item.dataTreinamento)}</TableCell><TableCell>{item.assunto}</TableCell><TableCell>{item.local}</TableCell><TableCell>{item.instrutor}</TableCell><TableCell>{item.anexo?.url ? <Button component="a" href={item.anexo.url} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>Ver anexo</Button> : "Não anexada"}</TableCell><TableCell><Button onClick={(event) => { event.stopPropagation(); history.push(`${ROTA}/${item.id}`); }}>Abrir</Button></TableCell></TableRow>)}
            {!aviso && filtrados.length === 0 && <TableRow><TableCell colSpan={6} align="center">Nenhuma lista de presença encontrada.</TableCell></TableRow>}
          </TableBody>
        </Table></Paper>}
        {!carregando && registroId && ficha && <Stack spacing={2}>
          <Paper sx={{ border: "1px solid #d7e0ea", p: 2 }}><Stack spacing={2}>
            {campo("assunto", "Assunto", { multiline: true, minRows: 2 })}
            {campo("conteudoProgramatico", "Conteúdo programático", { multiline: true, minRows: 4 })}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>{campo("dataTreinamento", "Data de realização do treinamento", { type: "date", InputLabelProps: { shrink: true } })}{campo("local", "Local de realização")}</Stack>
            {campo("instrutor", "Instrutor(a)")}
          </Stack></Paper>
          <Paper sx={{ border: "1px solid #d7e0ea", p: 2 }}><Stack spacing={2} alignItems="flex-start">
            <Typography fontWeight={700}>Lista de presença assinada</Typography>
            <Typography variant="body2" color="text.secondary">PDF menor que 100 MB ou imagem JPG, PNG ou WebP menor que 5 MB. O anexo será enviado ao salvar.</Typography>
            <Button component="label" variant="outlined" disabled={salvando}>{ficha.anexo || arquivo ? "Selecionar outro arquivo" : "Anexar lista assinada"}<input type="file" hidden accept="application/pdf,image/jpeg,image/png,image/webp" disabled={salvando} onChange={selecionarArquivo} /></Button>
            {arquivo && <Typography sx={{ overflowWrap: "anywhere" }}>Arquivo selecionado: {arquivo.name}</Typography>}
            {ficha.anexo?.url && <Button component="a" href={ficha.anexo.url} target="_blank" rel="noopener noreferrer">Abrir lista assinada: {ficha.anexo.nome}</Button>}
          </Stack></Paper>
        </Stack>}
      </Box>
    </>
  );
}
